import { tenantUsers } from '../models/tenantUsers';
import { TenantUser } from '../types/TenantUser';
import { Tenant } from '../types/Tenant';
import { tenants } from '../models/tenants';
import { BaseRepository } from '../../../classes/BaseRepository';
import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { eq, inArray } from 'drizzle-orm';
import { AppError, AsyncResult, err, ok } from '@shared/utils';
import { HTTP_STATUS_NAMES, DATA_API_MESSAGES } from '@shared/constants';

/**
 * @group User
 * @summary TenantUser repository
 * 
 * <blockquote>
 * Provides data access methods for tenant user management, including CRUD operations and tenant user-based permission checks.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const tenantUserRepository = new TenantUserRepository();
 * const tenantUser = await tenantUserRepository.assignUserToTenant('user-id', 'tenant-id');
 * ```
 * 
 */
export class TenantUserRepository extends BaseRepository<TenantUser> {
  protected table = tenantUsers;

  /**
   * Assigns a user to a tenant/group
   * 
   * @param userId - The user ID
   * @param tenantId - The tenant ID
   * @returns AsyncResult containing the created tenant user or error
   */
  async assignUserToTenant(userId: string, tenantId: string): AsyncResult<TenantUser, AppError> {
    if (!userId || !tenantId) {
      return err({
        errorMessage: 'User ID and tenant ID are required',
        errorCode: DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        statusCode: HTTP_STATUS_NAMES.BAD_REQUEST
      });
    }

    // Check if the user-tenant assignment already exists
    const existingAssignmentResult = await this.findByFields({
      user_id: userId,
      tenant_id: tenantId
    });

    if (existingAssignmentResult.isErr) {
      return err(existingAssignmentResult.getFail());
    }

    const existingAssignments = existingAssignmentResult.getSuccess();

    if (existingAssignments.length > 0) {
      return ok(existingAssignments[0]); // Return existing assignment
    }

    // Create new user-tenant assignment
    const createResult = await this.create({
      user_id: userId,
      tenant_id: tenantId
    });

    if (createResult.isErr) {
      return err(createResult.getFail());
    }

    return ok(createResult.getSuccess());
  }

  /**
   * Gets all tenants for a user
   * 
   * @param userId - The user ID
   * @returns AsyncResult containing tenant users or error
   */
  async getUserTenants(userId: string): AsyncResult<TenantUser[], AppError> {
    if (!userId) {
      return err({
        errorMessage: 'User ID is required',
        errorCode: DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        statusCode: HTTP_STATUS_NAMES.BAD_REQUEST
      });
    }

    return await this.findByFields({
      user_id: userId
    });
  }

  /**
   * Gets all users for a tenant
   * 
   * @param tenantId - The tenant ID
   * @returns AsyncResult containing tenant users or error
   */
  async getTenantUsers(tenantId: string): AsyncResult<TenantUser[], AppError> {
    if (!tenantId) {
      return err({
        errorMessage: 'Tenant ID is required',
        errorCode: DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        statusCode: HTTP_STATUS_NAMES.BAD_REQUEST
      });
    }

    return await this.findByFields({
      tenant_id: tenantId
    });
  }

  /**
   * Gets all tenant IDs owned by the specified user
   * 
   * Retrieves all tenant IDs owned by the user based on the `tenants.user_id` field.
   * This is used for scope resolution in tenant-scoped access control.
   * 
   * @param userId - The user ID to get owned tenant IDs for
   * @returns AsyncResult containing array of tenant IDs owned by the user, or error
   * 
   * @example
   * ```typescript
   * const tenantIdsResult = await repository.getOwnedTenantIds('user-123');
   * if (tenantIdsResult.isOk()) {
   *   const tenantIds = tenantIdsResult.value;
   *   console.log(`User owns ${tenantIds.length} tenants`);
   * }
   * ```
   */
  async getOwnedTenantIds(userId: string): AsyncResult<string[], AppError> {
    if (!userId) {
      return err({
        errorMessage: 'User ID is required',
        errorCode: DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        statusCode: HTTP_STATUS_NAMES.BAD_REQUEST
      });
    }

    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select({ id: tenants.id })
            .from(tenants)
            .where(eq(tenants.user_id, userId))
        );

        return queryResult.match({
          ok: (tenantRows) => {
            const ownedTenantIds = tenantRows.map(row => row.id);
            return ok(ownedTenantIds);
          },
          err: (error) =>
            err({
              errorMessage: error instanceof Error ? error.message : String(error),
            }),
        });
      },
      err: async (error) => err(error),
    });
  }

  /**
   * Gets all tenant IDs (owned + transitive) associated with the specified user
   * 
   * This method retrieves all tenant IDs that should be included in the user's scope:
   * 1. Tenants owned by the user (where tenants.user_id = userId)
   * 2. Tenants owned by members of tenants that the user owns (transitive access)
   * 
   * Important: Being a member of a tenant does NOT grant access to that tenant.
   * Only tenants you own (and transitive tenants) are included in the scope.
   * 
   * Example:
   * - Tenant A admin (owns Tenant A): sees Tenant A + Tenant B (if member of Tenant A owns Tenant B)
   * - Tenant B admin (owns Tenant B, member of Tenant A): sees ONLY Tenant B (not Tenant A)
   * 
   * @param userId - The user ID
   * @returns AsyncResult containing array of tenant IDs (owned + transitive), or error
   * 
   * @example
   * ```typescript
   * const tenantIdsResult = await repository.getOwnedAndTransitiveTenantIds('owner-123');
   * if (tenantIdsResult.isOk()) {
   *   const tenantIds = tenantIdsResult.value;
   *   console.log(`Found ${tenantIds.length} tenants in associated tenant scope`);
   * }
   * ```
   */
  async getOwnedAndTransitiveTenantIds(userId: string): AsyncResult<string[], AppError> {
    if (!userId) {
      return err({
        errorMessage: 'Owner user ID is required',
        errorCode: DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        statusCode: HTTP_STATUS_NAMES.BAD_REQUEST
      });
    }

    return db.match({
      ok: async (database) => {
        // Get owned tenant IDs
        const ownedTenantsResult = await retryableQuery(() =>
          database
            .select({ id: tenants.id })
            .from(tenants)
            .where(eq(tenants.user_id, userId))
        );

        return ownedTenantsResult.match({
          ok: async (ownedTenantRows) => {
            const ownedTenantIds = ownedTenantRows.map(row => row.id);

            // Get transitive tenant IDs (tenants owned by members of owned tenants)
            // This allows Tenant A's admin to see Tenant B's data if a member of Tenant A owns Tenant B
            let transitiveTenantIds: string[] = [];
            if (ownedTenantIds.length > 0) {
              // Get all members of tenants that the user owns
              const memberUsersResult = await retryableQuery(() =>
                database
                  .select({ user_id: tenantUsers.user_id })
                  .from(tenantUsers)
                  .where(inArray(tenantUsers.tenant_id, ownedTenantIds))
              );

              return memberUsersResult.match({
                ok: async (memberUserRows) => {
                  const memberUserIds = Array.from(new Set(memberUserRows.map(row => row.user_id)));
                  if (memberUserIds.length > 0) {
                    // Get tenants owned by those members (transitive tenants)
                    const transitiveTenantsResult = await retryableQuery(() =>
                      database
                        .select({ id: tenants.id })
                        .from(tenants)
                        .where(inArray(tenants.user_id, memberUserIds))
                    );

                    return transitiveTenantsResult.match({
                      ok: (transitiveTenantRows) => {
                        transitiveTenantIds = transitiveTenantRows.map(row => row.id);

                        // Combine owned and transitive tenant IDs (remove duplicates)
                        // Note: Member tenant IDs are NOT included - being a member doesn't grant access
                        const allTenantIds = Array.from(
                          new Set([...ownedTenantIds, ...transitiveTenantIds])
                        );

                        return Promise.resolve(ok(allTenantIds));
                      },
                      err: (error) => Promise.resolve(err(error)),
                    });
                  }

                  // No transitive tenants, just return owned tenants
                  return Promise.resolve(ok(ownedTenantIds));
                },
                err: (error) => Promise.resolve(err(error)),
              });
            }

            // No owned tenants, return empty array
            return Promise.resolve(ok([]));
          },
          err: (error) => Promise.resolve(err(error)),
        });
      },
      err: async (error) => err(error),
    });
  }

  /**
   * Gets all user IDs linked to tenants associated with the specified user
   * 
   * This method retrieves all users who are members of:
   * 1. Tenants owned by the user (where tenants.user_id = userId)
   * 2. Tenants owned by members of tenants that the user owns (transitive access)
   * 
   * Important: Being a member of a tenant does NOT grant access to that tenant's users.
   * Only tenants you own (and transitive tenants) are included in the scope.
   * 
   * Example:
   * - Tenant A admin (owns Tenant A): sees Tenant A users + Tenant B users (if member of Tenant A owns Tenant B)
   * - Tenant B admin (owns Tenant B, member of Tenant A): sees ONLY Tenant B users (not Tenant A users)
   * 
   * @param userId - The user ID
   * @returns AsyncResult containing array of user IDs linked to associated tenants, or error
   * 
   * @example
   * ```typescript
   * const usersResult = await repository.getUsersLinkedToOwnedTenants('owner-123');
   * if (usersResult.isOk()) {
   *   const userIds = usersResult.value;
   *   console.log(`Found ${userIds.length} users in associated tenant scope`);
   * }
   * ```
   */
  async getUsersLinkedToOwnedTenants(userId: string): AsyncResult<string[], AppError> {
    // Get all tenant IDs (owned + transitive)
    const tenantIdsResult = await this.getOwnedAndTransitiveTenantIds(userId);
    if (tenantIdsResult.isErr) {
      return err(tenantIdsResult.getFail());
    }

    const allTenantIds = tenantIdsResult.getSuccess();
    
    if (allTenantIds.length === 0) {
      return ok([]);
    }

    // Get all users from these tenants
    return db.match({
      ok: async (database) => {
        const usersResult = await retryableQuery(() =>
          database
            .select({ user_id: tenantUsers.user_id })
            .from(tenantUsers)
            .where(inArray(tenantUsers.tenant_id, allTenantIds))
        );

        return usersResult.match({
          ok: (tenantUserRows) => {
            const userIds = Array.from(new Set(tenantUserRows.map(row => row.user_id)));
            return ok(userIds);
          },
          err: (error) =>
            err({
              errorMessage: error instanceof Error ? error.message : String(error),
            }),
        });
      },
      err: async (error) => err(error),
    });
  }

  /**
   * Gets all user IDs from tenants that the specified user is a member of
   * 
   * This method retrieves all users who are members of the same tenants as the specified user.
   * It first gets all tenant IDs the user is a member of, then queries the TenantUsers
   * junction table for all users linked to those tenants.
   * 
   * @param userId - The user ID to get tenant memberships for
   * @returns AsyncResult containing array of user IDs from the same tenants, or error
   * 
   * @example
   * ```typescript
   * const usersResult = await repository.getUsersFromMemberTenants('user-123');
   * if (usersResult.isOk()) {
   *   const userIds = usersResult.value;
   *   console.log(`Found ${userIds.length} users in same tenant scope`);
   * }
   * ```
   */
  async getUsersFromMemberTenants(userId: string): AsyncResult<string[], AppError> {
    if (!userId) {
      return err({
        errorMessage: 'User ID is required',
        errorCode: DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        statusCode: HTTP_STATUS_NAMES.BAD_REQUEST
      });
    }

    // Get tenant IDs that the user is a member of
    const userTenantsResult = await this.getUserTenants(userId);
    if (userTenantsResult.isErr) {
      return err(userTenantsResult.getFail());
    }

    const userTenants = userTenantsResult.getSuccess();
    
    // If user is not a member of any tenant, return empty array
    if (userTenants.length === 0) {
      return ok([]);
    }

    const memberTenantIds = userTenants.map(tu => tu.tenant_id);

    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select({ user_id: tenantUsers.user_id })
            .from(tenantUsers)
            .where(inArray(tenantUsers.tenant_id, memberTenantIds))
        );

        return queryResult.match({
          ok: (tenantUserRows) => {
            // Extract unique user IDs
            const userIds = Array.from(new Set(tenantUserRows.map(row => row.user_id)));
            return ok(userIds);
          },
          err: (error) =>
            err({
              errorMessage: error instanceof Error ? error.message : String(error),
            }),
        });
      },
      err: async (error) => err(error),
    });
  }

  /**
   * Gets tenant-user relationships with tenant details for multiple users
   * 
   * @param userIds - Array of user IDs
   * @returns AsyncResult containing tenant-user relationships with tenant details or error
   * 
   * @example
   * ```typescript
   * const tenantUsersResult = await repository.getUserTenantsByUserIds(['user-1', 'user-2']);
   * if (tenantUsersResult.isOk()) {
   *   const tenantUsers = tenantUsersResult.value;
   *   // tenantUsers is Array<{ user_id: string; tenant: Tenant }>
   * }
   * ```
   */
  async getUserTenantsByUserIds(userIds: string[]): AsyncResult<Array<{ user_id: string; tenant: Tenant }>, AppError> {
    if (!userIds || userIds.length === 0) {
      return ok([]);
    }

    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select({
              user_id: tenantUsers.user_id,
              tenant: tenants
            })
            .from(tenantUsers)
            .innerJoin(tenants, eq(tenantUsers.tenant_id, tenants.id))
            .where(inArray(tenantUsers.user_id, userIds))
        );

        return queryResult.match({
          ok: (tenantUsersWithTenants) => ok(tenantUsersWithTenants),
          err: (error) => err({
            errorMessage: error instanceof Error ? error.message : String(error),
          })
        });
      },
      err: async (error) => err(error),
    });
  }
}
